"use client";

/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useState } from "react";
import { StyledDiv } from "./blogPostHero.styles";
import { IBlogPage } from "../../types";
import { defineCustomElements as deckDeckGoHighlightElement } from "@deckdeckgo/highlight-code/dist/loader";
import { Banner } from "../banner/banner.comp";
import { CustomLink } from "../link/link.comp";
import Link from "next/link";
import Image from "next/image";
import { CSSVars } from "../../styles/styles.config";
import { IoIosArrowForward } from "@react-icons/all-files/io/IoIosArrowForward";
import { CommonNinjaWidgetRenderer } from "../CommonNinjaWidgetRenderer/commonNinjaWidgetRenderer.comp";
import { getBlogDisplayDates } from "../../helpers/blogDates";
import { BlogCardDate } from "../blogPosts/blogCardDate.comp";

// Code highlighter
deckDeckGoHighlightElement();

interface IBlogPostHeroProps {
  blogPost: IBlogPage;
  suggestions: IBlogPage[];
}

export const BlogPostHero = ({
  blogPost,
  suggestions = [],
}: IBlogPostHeroProps) => {
  const [postContent, setPostContent] = useState(blogPost.postContent);
  const [h2Tags, setH2Tags] = useState<string[]>([]);
  const [activeAnchor, setActiveAnchor] = useState("");

  function handleAffiliateLinkClick(event: any) {
    const affiliateUrl = event.target.getAttribute("data-href");
    if (affiliateUrl) {
      const newTab = window.open("", "_blank");
      if (newTab) {
        newTab.opener = null;
        const newLink = newTab.document.createElement("a");
        newLink.href = affiliateUrl;
        newLink.rel = "nofollow";
        newTab.document.body.appendChild(newLink);
        newLink.click();
        newTab.document.body.removeChild(newLink);
      }
    }
  }

  function extractH2TagsFromHtml() {
    const allTags = [] as any;

    const formattedPost = blogPost.postContent.map((section) => {
      if (section.type === "rich-text" || section.type === "code") {
        const parser = new DOMParser();
        const doc = parser.parseFromString(section.content || "", "text/html");
        const h2Elements = doc.querySelectorAll("h2");
        const aTagElements = doc.querySelectorAll("a");

        h2Elements.forEach((h2, index) => {
          const textContent = h2.textContent
            ?.replaceAll(" ", "-")
            .replace(/^\s*[\r\n]/gm, "")
            .replace(/\n/g, "")
            .replace(/^--/gm, "");
          if (textContent) {
            h2.setAttribute("id", `${textContent}`);
            allTags.push(textContent);
          }
        });

        aTagElements.forEach((a, index) => {
          const { hostname, protocol } = new URL(a.href);

          if (a.href.includes("cloak")) {
            a.setAttribute("data-href", a.href.replace("cloak", ""));
            a.setAttribute("href", `${protocol}//${hostname}`);
            a.setAttribute("rel", "nofollow");
          }
        });

        return { ...section, content: doc?.documentElement?.innerHTML || "" };
      }

      return section;
    });
    setPostContent(formattedPost);
    setH2Tags(allTags);
  }

  function renderMightLikeSection() {
    return (
      <div className="might-like">
        <h3> You Might Also Like:</h3>
        <div className="post-cards-wrapper">
          {suggestions.map((post: IBlogPage) => (
            <Link
              key={post.slug}
              title={`${post.name}`}
              href={`/blog/${post.slug}`}
            >
              <div className={`post`}>
                <Image
                  width={320}
                  height={180}
                  src={`${post.coverImage || ""}`}
                  alt={post.name}
                />
                <div className="details">
                  <h3>{post.name}</h3>
                  <p className="author">
                    {post.author},
                    <BlogCardDate post={post} />
                  </p>
                  {/* <div className="desc" dangerouslySetInnerHTML={{ __html: paginatedPosts[0].excerpt }}></div> */}
                  <button className="read-more">
                    Read More
                    <IoIosArrowForward className="icon" />
                  </button>
                </div>
              </div>
            </Link>
            // <Link
            //   key={post.slug}
            //   title={`${post.name}`}
            //   href={`/blog/${post.slug}`}
            // >
            //   <div className="post-card">
            //     <Image
            //       width={320}
            //       height={180}
            //       src={`${post.coverImage || ""}`}
            //       alt={post.name}
            //     />
            //     <h4>{post.name}</h4>
            //   </div>
            // </Link>
          ))}
        </div>
      </div>
    );
  }

  useEffect(() => {
    extractH2TagsFromHtml();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogPost]);

  const handleScroll = () => {
    for (const anchor of h2Tags) {
      const element = document.getElementById(anchor);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top >= 0 && rect.top < 100) {
          setActiveAnchor(anchor);
          break;
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [h2Tags]);

  useEffect(() => {
    const anchorTags = document.querySelectorAll("[data-href]");
    anchorTags.forEach((a) => {
      a.addEventListener("click", handleAffiliateLinkClick);
    });

    return () => {
      anchorTags.forEach((a) => {
        a.removeEventListener("click", handleAffiliateLinkClick);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postContent]);

  useEffect(() => {
    if (activeAnchor) {
      window.location.hash = activeAnchor;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAnchor]);

  // Fire on a blog in-content CTA banner click, so bridge CTAs (e.g. the Google
  // Reviews banner on the reviews-search post) can be measured per-page.
  // Additive only: never blocks or alters the navigation.
  function trackBlogCtaClick(section: IBlogPage["postContent"][0]) {
    if (typeof window === "undefined") return;
    const ctaText = section.bannerButtonText || section.bannerText || "";
    const ctaLink = section.bannerButtonLink || "";

    // 1) GTM/GA4 dataLayer event (needs a GTM trigger to reach GA4).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "blog_cta_click",
      blog_slug: blogPost.slug,
      cta_text: ctaText,
      cta_link: ctaLink,
    });

    // 2) Self-owned sink -> our MongoDB (blog_cta_events), mirroring the
    // analyzer/template/playground trackers. Pullable directly, no GTM/GA4 read
    // needed. keepalive so it survives the navigation; fail-quiet.
    try {
      fetch("/api/blog/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          blogSlug: blogPost.slug,
          ctaText,
          ctaLink,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      /* never let analytics break the interaction */
    }
  }

  function renderContent(section: IBlogPage["postContent"][0]) {
    switch (section.type) {
      case "rich-text":
      case "code":
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: (section.content || "")
                .replace(/blog.commoninja.com/g, "www.commoninja.com/blog/")
                .replace(
                  /https:\/\/www.commoninja.com\/blog\/\/wp-content/g,
                  "https://blog.commoninja.com/wp-content"
                )
                .replace(
                  /<pre([^>]*)>([\s\S]*?)<\/pre>/g,
                  '<deckgo-highlight-code><code slot="code">$2</code></deckgo-highlight-code>'
                ),
            }}
          ></div>
        );

      case "banner":
        return (
          <Banner
            size={section.bannerSize}
            style={{ padding: "0", margin: "50px 0" }}
            title={section.bannerTitle || ""}
            text={section.bannerText || ""}
            bannerStyle={{
              backgroundColor: section.bannerColor,
              color:
                section.bannerColor === "#293258" ||
                section.bannerColor === "#171B2C"
                  ? "#fff"
                  : "#141414",
              padding:
                section.bannerSize === "small" ? "25px 15px" : "50px 30px",
            }}
            openInNewWindow
            buttonLink={section.bannerButtonLink}
            buttonText={section.bannerButtonText}
            onButtonClick={() => trackBlogCtaClick(section)}
          />
        );

      case "button":
        return (
          <CustomLink
            className={`cta ${section.buttonType}`}
            style={{
              color:
                section.buttonColor && section.buttonType === "link"
                  ? section.buttonColor
                  : `var(${CSSVars.blue500})`,
            }}
            href={section.buttonLink}
            target={section.openInNewTab ? "_blank" : "_self"}
          >
            {section.buttonText}
          </CustomLink>
        );

      case "widget":
        return (
          <>
            {section.pluginId && (
              <div className="html">
                <CommonNinjaWidgetRenderer widgetId={section.pluginId} />
              </div>
            )}
          </>
        );

      case "quotes":
        return (
          <>
            <div className="quotes-container">
              {section.quotesImg && (
                <img
                  src={section.quotesImg}
                  alt={section.quotesTitle || "Profile thumbnail"}
                />
              )}
              <div className="title">
                {section.quotesTitle && <span>{section.quotesTitle}</span>}
                {section.quotesText && (
                  <div
                    className="quotes-text"
                    dangerouslySetInnerHTML={{
                      __html: (section.quotesText || "")
                        .replace(
                          /blog.commoninja.com/g,
                          "www.commoninja.com/blog/"
                        )
                        .replace(
                          /https:\/\/www.commoninja.com\/blog\/\/wp-content/g,
                          "https://blog.commoninja.com/wp-content"
                        )
                        .replace(
                          /<pre([^>]*)>([\s\S]*?)<\/pre>/g,
                          '<deckgo-highlight-code><code slot="code">$2</code></deckgo-highlight-code>'
                        ),
                    }}
                  ></div>
                )}
              </div>
            </div>
          </>
        );

      default:
        return <></>;
    }
  }

  const postDates = getBlogDisplayDates(blogPost);

  return (
    <>
      <StyledDiv>
        <div className="hero-wrapper">
          <div className="inner-wrapper">
            <h1>{blogPost.name}</h1>
            <h5>
              {blogPost.author},
              <time dateTime={postDates.primary.iso}>
                {" "}
                {postDates.primary.isUpdate && "Updated "}
                {postDates.primary.label}
              </time>
            </h5>
            {postDates.published && (
              <p className="published-date">
                Originally published{" "}
                <time dateTime={postDates.published.iso}>
                  {postDates.published.label}
                </time>
              </p>
            )}
            <div className="tags">
              {blogPost.categories.map((cat) => {
                if (!cat.name) return;
                return (
                  <Link
                    key={`cat_${cat.name}`}
                    className="tag"
                    href={`/blog/category/${((cat.name as string) || "")
                      .toLowerCase()
                      .replaceAll(" ", "-")}`}
                  >
                    {cat.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
        <div className="blog-post-content">
          <>
            {blogPost.showTldr && (
              <p className="tldr">
                {!blogPost?.disableSummaryPrefix && (
                  <strong>Summary (TL;DR): </strong>
                )}
                {blogPost.summary}
              </p>
            )}
            <img
              src={blogPost.coverImage}
              alt={blogPost.name}
              className="main-img"
            />
            <div className="content-layout">
              {h2Tags.length > 0 && (
                <div className="side-banner">
                  <div className="table-of-contents">
                    <p className="title">Table of Contents</p>
                    {h2Tags.map((tag, idx) => {
                      return (
                        <a
                          key={`${tag}-${idx}`}
                          href={`#${tag}`}
                          className={activeAnchor === tag ? "active" : ""}
                        >
                          <div></div> {tag.replaceAll("-", " ")}
                        </a>
                      );
                    })}
                  </div>
                  {blogPost.sideBannerButtonText &&
                    blogPost.sideBannerButtonLink &&
                    blogPost.sideBannerTitle && (
                      <div
                        style={{ backgroundColor: blogPost.sideBannerBg }}
                        className="banner"
                      >
                        {blogPost.sideBannerCoverImage && (
                          <img
                            src={blogPost.sideBannerCoverImage}
                            alt={"banner cover"}
                          />
                        )}
                        {blogPost.sideBannerTitle && (
                          <p
                            style={{ color: blogPost.sideBannerTextColor }}
                            className="title"
                          >
                            {blogPost.sideBannerTitle}
                          </p>
                        )}
                        {blogPost.sideBannerText && (
                          <p
                            style={{ color: blogPost.sideBannerTextColor }}
                            className="text"
                          >
                            {blogPost.sideBannerText}
                          </p>
                        )}
                        {blogPost.sideBannerButtonText &&
                          blogPost.sideBannerButtonLink && (
                            <CustomLink
                              className={`cta`}
                              style={{
                                color: blogPost.sideBannerButtonColor,
                                backgroundColor: blogPost.sideBannerButtonBg,
                              }}
                              href={blogPost.sideBannerButtonLink}
                              target={
                                blogPost.sideBannerOpenInNewTab
                                  ? "_blank"
                                  : "_self"
                              }
                            >
                              {blogPost.sideBannerButtonText}
                            </CustomLink>
                          )}
                      </div>
                    )}
                </div>
              )}
              <main className="content">
                {postContent.map((section, idx) => (
                  <React.Fragment key={idx}>
                    {renderContent(section)}
                  </React.Fragment>
                ))}
                
                {/* Author Section */}
                <div className="author-section">
                  <div className="author-image">
                    <img
                      src="https://website-assets.commoninja.com/distribution/sergei_image.webp"
                      alt="Sergei Davidov"
                    />
                  </div>
                  <div className="author-info">
                    <h4 className="author-name">Sergei Davidov</h4>
                    <p className="author-description">
                      Sergei Davidov is a Growth Manager at Common Ninja with nearly a decade of experience spanning content strategy, SEO, conversion optimization, and business development. He&apos;s helped launch products, optimize funnels, and build marketing systems across e-commerce and SaaS. When he&apos;s not dissecting funnel metrics, he writes fiction and experiments in the kitchen.
                    </p>
                    <div className="author-socials">
                      <a
                        href="https://www.linkedin.com/in/sergei-davidov1"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn"
                      >
                        <img
                          src="https://website-assets.commoninja.com/distribution/linkedin_icon.webp"
                          alt="LinkedIn"
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </main>
            </div>
          </>
        </div>
        {renderMightLikeSection()}
      </StyledDiv>
    </>
  );
};
